/**
 * KAN-660: geometry of the day timeline in the "Рассылка сегодня" block.
 *
 * Pure functions: a moment is turned into a position on a 00:00-24:00 bar
 * (percent of the bar width) in the time zone of the sending window. Time
 * zone math goes through Intl, no extra dependencies.
 */

export const DAY_MINUTES = 24 * 60

/** Hours labelled under the bar. */
export const HOUR_LABELS = [0, 6, 12, 18, 24]

const FALLBACK_ZONE = 'Europe/Moscow'

export interface TimelineWindow {
  /** "HH:MM" in the window time zone. */
  start: string
  end: string
  timezone: string
  /** KAN-664: closed now and opening later (ISO) - planned recipients start from `opens_at`. */
  open?: boolean
  opens_at?: string | null
}

export interface TimelineSent {
  at: string
  account_id: string
  label: string
  /** KAN-664: who the message went to (absent on an older backend). */
  recipient?: string
}

/** KAN-664: one planned recipient (planner order). */
export interface TimelinePlanned {
  record_id: string
  name: string
  phone: string
  campaign: string
}

export type HourKind = 'past' | 'current' | 'future'

export interface HourSent {
  /** "HH:MM" in the window time zone. */
  time: string
  recipient: string
  label: string
  at: string
}

export interface HourBucket {
  hour: number
  kind: HourKind
  /** The hour overlaps the sending window. */
  inWindow: boolean
  sent: HourSent[]
  planned: TimelinePlanned[]
}

export interface TimelineMark {
  percent: number
  at: string
  label: string
  /** "HH:MM" in the window time zone. */
  time: string
}

export interface DayTimeline {
  timezone: string
  window: { startPercent: number; endPercent: number } | null
  /** Filled part: from the window start to now (or to the window end once it passed). */
  fill: { fromPercent: number; toPercent: number } | null
  nowPercent: number
  /** "HH:MM" of now in the window time zone. */
  nowTime: string
  nowInWindow: boolean
  marks: TimelineMark[]
}

/** Short zone name for labels: Moscow as "МСК", anything else as the IANA name. */
export function zoneLabel(timezone: string): string {
  return timezone === 'Europe/Moscow' ? 'МСК' : timezone
}

/** "HH:MM" -> minutes since midnight; anything unparsable -> 0. */
export function clockToMinutes(clock: string): number {
  const match = /^(\d{1,2}):(\d{2})$/.exec(clock.trim())
  if (!match) return 0
  const hours = Math.min(24, Number(match[1]))
  const minutes = Math.min(59, Number(match[2]))
  return Math.min(DAY_MINUTES, hours * 60 + minutes)
}

function partsInZone(date: Date, timeZone: string): { hour: number; minute: number } {
  let formatter: Intl.DateTimeFormat
  try {
    formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
  } catch {
    formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: FALLBACK_ZONE,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
  }
  const parts = formatter.formatToParts(date)
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0) % 24
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0)
  return { hour, minute }
}

/** "YYYY-MM-DD" of `date` in `timeZone` (same-day checks); falls back to Moscow on an unknown zone. */
export function dayInZone(date: Date, timeZone: string): string {
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' }
  let formatter: Intl.DateTimeFormat
  try {
    formatter = new Intl.DateTimeFormat('en-CA', { timeZone, ...options })
  } catch {
    formatter = new Intl.DateTimeFormat('en-CA', { timeZone: FALLBACK_ZONE, ...options })
  }
  return formatter.format(date)
}

/** Minutes since local midnight of `date` in `timeZone`. */
export function minutesInZone(date: Date, timeZone: string): number {
  const { hour, minute } = partsInZone(date, timeZone)
  return hour * 60 + minute
}

/** "HH:MM" of `date` in `timeZone`. */
export function clockInZone(date: Date, timeZone: string): string {
  const { hour, minute } = partsInZone(date, timeZone)
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

/** Minutes of the day -> percent of the bar, clamped to 0..100. */
export function minutesToPercent(minutes: number): number {
  const clamped = Math.max(0, Math.min(DAY_MINUTES, minutes))
  return (clamped / DAY_MINUTES) * 100
}

/** Hour (0..24) -> percent of the bar. */
export function hourToPercent(hour: number): number {
  return minutesToPercent(hour * 60)
}

/** KAN-679: "13:00-14:00" for hour 13 (zone labels and the hovered-hour caption). */
export function hourRangeLabel(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00-${String(hour + 1).padStart(2, '0')}:00`
}

function parseDate(iso: string): Date | null {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Window bounds in minutes of the day. A window whose end is not after its
 * start (never open, or crossing midnight) lasts to the end of the day.
 */
export function windowMinutes(window: TimelineWindow): { startMinutes: number; endMinutes: number } {
  const startMinutes = clockToMinutes(window.start)
  const rawEnd = clockToMinutes(window.end)
  return { startMinutes, endMinutes: rawEnd > startMinutes ? rawEnd : DAY_MINUTES }
}

/**
 * Build the geometry of the day bar.
 *
 * A window whose end is not after its start (never open, or crossing midnight)
 * is drawn from its start to the end of the day.
 */
export function buildDayTimeline(input: {
  window: TimelineWindow | null
  sentTimes: TimelineSent[]
  now: Date
}): DayTimeline {
  const timezone = input.window?.timezone || FALLBACK_ZONE
  const nowMinutes = minutesInZone(input.now, timezone)
  const nowPercent = minutesToPercent(nowMinutes)
  const nowTime = clockInZone(input.now, timezone)

  let window: DayTimeline['window'] = null
  let fill: DayTimeline['fill'] = null
  let nowInWindow = false
  if (input.window) {
    const { startMinutes, endMinutes } = windowMinutes(input.window)
    window = { startPercent: minutesToPercent(startMinutes), endPercent: minutesToPercent(endMinutes) }
    nowInWindow = nowMinutes >= startMinutes && nowMinutes < endMinutes
    if (nowMinutes >= startMinutes) {
      fill = {
        fromPercent: window.startPercent,
        toPercent: minutesToPercent(Math.min(nowMinutes, endMinutes)),
      }
    }
  }

  const marks: TimelineMark[] = []
  for (const sent of input.sentTimes) {
    const date = parseDate(sent.at)
    if (!date) continue
    marks.push({
      percent: minutesToPercent(minutesInZone(date, timezone)),
      at: sent.at,
      label: sent.label,
      time: clockInZone(date, timezone),
    })
  }

  return { timezone, window, fill, nowPercent, nowTime, nowInWindow, marks }
}

export const HOURS = Array.from({ length: 24 }, (_, hour) => hour)

/**
 * KAN-664: minutes of the day from which planned recipients are spread.
 *
 * From max(now, window start); a closed window that opens later today moves
 * the start to `opens_at`; a window that opens on another day leaves no room
 * today. Without a window: from now.
 */
function plannedFrom(window: TimelineWindow | null, now: Date, timezone: string): { from: number; to: number } {
  const nowMinutes = minutesInZone(now, timezone)
  if (!window) return { from: nowMinutes, to: DAY_MINUTES }
  const { startMinutes, endMinutes } = windowMinutes(window)
  let from = Math.max(nowMinutes, startMinutes)
  if (window.open === false && window.opens_at) {
    const opensAt = parseDate(window.opens_at)
    if (opensAt && dayInZone(opensAt, timezone) === dayInZone(now, timezone)) {
      from = Math.max(from, minutesInZone(opensAt, timezone))
    } else {
      from = endMinutes
    }
  }
  return { from, to: endMinutes }
}

/**
 * KAN-664: 24 hour buckets for the hour tooltips.
 *
 * Sent messages go to the hour of `at` in the window zone. Planned recipients
 * are spread evenly over the remaining window time: interval = remaining
 * minutes / planned.length, recipient i lands in hour floor((from + i * interval) / 60).
 */
export function hourBuckets(
  sent: TimelineSent[],
  planned: TimelinePlanned[],
  window: TimelineWindow | null,
  now: Date,
  timezone?: string
): HourBucket[] {
  const zone = timezone || window?.timezone || FALLBACK_ZONE
  const nowHour = Math.floor(minutesInZone(now, zone) / 60)
  const bounds = window ? windowMinutes(window) : null
  const buckets: HourBucket[] = HOURS.map((hour) => ({
    hour,
    kind: hour < nowHour ? 'past' : hour === nowHour ? 'current' : 'future',
    inWindow: bounds ? hour * 60 < bounds.endMinutes && (hour + 1) * 60 > bounds.startMinutes : false,
    sent: [],
    planned: [],
  }))

  for (const mark of sent) {
    const date = parseDate(mark.at)
    if (!date) continue
    const minutes = minutesInZone(date, zone)
    buckets[Math.min(23, Math.floor(minutes / 60))].sent.push({
      time: clockInZone(date, zone),
      recipient: mark.recipient || 'получатель',
      label: mark.label,
      at: mark.at,
    })
  }

  const { from, to } = plannedFrom(window, now, zone)
  const remaining = to - from
  if (planned.length > 0 && remaining > 0) {
    const interval = remaining / planned.length
    planned.forEach((item, index) => {
      const hour = Math.min(23, Math.floor((from + index * interval) / 60))
      buckets[hour].planned.push(item)
    })
  }
  return buckets
}
